import { useEffect, useMemo, useRef, useState } from "react";
import { FiX, FiLoader, FiImage, FiTrash2 } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { CONSULTING_APP_ID } from "../utils/mintSpaceApps";
import { updateAppPricing, deleteAppPricing } from "../api/mintSpace";
import { uploadImage } from "../api/upload";
import ConfirmModal from "./ConfirmModal";
import { saveConsultingCalendarSettings, getConsultingCalendarSettings } from "../api/mintSpaceConsulting";
import { CONSULTING_TIMEZONES } from "../constants/consultingTimezones";
import { PopUpSelect } from "./ds/PopUpButton";

const WEEK_DAYS = [
  { id: 1, label: "Mon" },
  { id: 2, label: "Tue" },
  { id: 3, label: "Wed" },
  { id: 4, label: "Thu" },
  { id: 5, label: "Fri" },
  { id: 6, label: "Sat" },
  { id: 0, label: "Sun" },
];

const defaultWeekly = () =>
  [1, 2, 3, 4, 5].map((day) => ({ day, on: true, start: "10:00", end: "18:00" }));

/**
 * Consulting-only create/edit form — stays inside the Consulting app (no generic Pricing wizard).
 */
export default function ConsultingOfferingForm({
  isDark,
  setToast,
  mintSpace,
  appInstanceId,
  productId,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const spaceId = mintSpace?._id;
  const isEdit = Boolean(productId);
  const fileRef = useRef(null);

  const existing = useMemo(
    () => (mintSpace?.appPricing || []).find((p) => String(p._id) === String(productId)),
    [mintSpace, productId]
  );

  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [accessType, setAccessType] = useState("paid");
  const [price, setPrice] = useState("49");
  const [slotMinutes, setSlotMinutes] = useState("45");
  const [timezone, setTimezone] = useState("Asia/Tbilisi");
  const [weeklyUi, setWeeklyUi] = useState(defaultWeekly);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [excludedStart, setExcludedStart] = useState("17:00");
  const [excludedEnd, setExcludedEnd] = useState("18:00");
  const [useExcludedBlock, setUseExcludedBlock] = useState(false);
  const [listingBanner, setListingBanner] = useState("");
  const [listingPreview, setListingPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!existing) {
      setName("");
      setHeadline("");
      setDescription("");
      setAccessType("paid");
      setPrice("49");
      setSlotMinutes("45");
      setWeeklyUi(defaultWeekly());
      setIncludeWeekends(false);
      setListingBanner("");
      setListingPreview(null);
      return;
    }
    setName(existing.name || "");
    setHeadline(existing.headline || "");
    setDescription(existing.description || "");
    const free = existing.accessType === "free" || Number(existing.price) <= 0;
    setAccessType(free ? "free" : "paid");
    setPrice(String(existing.price ?? 49));
    setSlotMinutes(String(existing.consultingSlotMinutes || 45));
    setListingBanner(existing.listingBanner || existing.bannerImage || "");
    setListingPreview(existing.listingBanner || existing.bannerImage || null);
  }, [existing, productId]);

  useEffect(() => {
    if (!spaceId || !productId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getConsultingCalendarSettings(spaceId, productId);
        if (!res.success || cancelled) return;
        const s = res.data.settings || {};
        setTimezone(s.timezone || "Asia/Tbilisi");
        setSlotMinutes(String(s.slotMinutes || existing?.consultingSlotMinutes || 45));
        const map = new Map((s.weekly || []).map((w) => [w.day, w]));
        setWeeklyUi(
          WEEK_DAYS.map(({ id }) => ({
            day: id,
            on: map.has(id),
            start: map.get(id)?.start || "10:00",
            end: map.get(id)?.end || "18:00",
          }))
        );
        setIncludeWeekends(map.has(0) || map.has(6));
        const ex = (s.excludedHours || [])[0];
        if (ex) {
          setUseExcludedBlock(true);
          setExcludedStart(ex.start || "17:00");
          setExcludedEnd(ex.end || "18:00");
        }
      } catch {
        /* defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceId, productId, existing?.consultingSlotMinutes]);

  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const panel = "border-separator-default bg-bg-raised";
  const input = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setToast?.(t("actualHome.imageSizeMustBeLessThan10MB") || "Image max 10MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImage(file, "app-pricing");
      if (!res?.success || !res.url) throw new Error("Upload failed");
      setListingBanner(res.url);
      setListingPreview(res.url);
    } catch (err) {
      setToast?.(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setToast?.(t("consulting.offeringNameRequired") || "Add a consultation name");
      return;
    }
    if (!spaceId || !appInstanceId) return;

    const payload = {
      appInstanceIds: [appInstanceId],
      appId: CONSULTING_APP_ID,
      appInstanceId,
      productId: productId || undefined,
      name: name.trim(),
      headline: headline.trim(),
      description: description.trim(),
      accessType,
      billingType: "one-time",
      price: accessType === "free" ? 0 : Math.max(0, Number(price) || 0),
      period: "1m",
      currency: "GEL",
      freeTrial: false,
      trialDays: 0,
      billingCycles: 0,
      spotsLimit: 0,
      buttonText: "",
      bannerImage: listingBanner,
      bannerType: "image",
      listingBanner,
      listingBannerType: "image",
      faq: [],
      consultingBookingFlow: "book_then_pay",
      consultingSlotMinutes: Math.min(180, Math.max(15, Math.floor(Number(slotMinutes) || 45))),
      consultingAccessMode: "",
      consultingDurationDays: 30,
      consultingCallQuota: 1,
    };

    setSaving(true);
    try {
      const res = await updateAppPricing(spaceId, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");

      const savedProductId = res.productId || productId || res.data?.appPricing?.slice(-1)?.[0]?._id;
      const pid =
        savedProductId ||
        (res.data?.appPricing || []).find((p) => p.name === name.trim())?._id;
      if (pid && appInstanceId) {
        let weekly = weeklyUi.filter((r) => r.on).map(({ day, start, end }) => ({ day, start, end }));
        if (includeWeekends) {
          [0, 6].forEach((day) => {
            if (!weekly.some((w) => w.day === day)) {
              weekly.push({ day, start: "10:00", end: "18:00" });
            }
          });
        } else {
          weekly = weekly.filter((w) => w.day !== 0 && w.day !== 6);
        }
        await saveConsultingCalendarSettings(
          spaceId,
          pid,
          {
            enabled: true,
            timezone,
            weekly,
            slotMinutes: Math.min(180, Math.max(15, Number(slotMinutes) || 45)),
            bufferMinutes: 10,
            minNoticeHours: 3,
            excludedHours:
              useExcludedBlock && excludedStart && excludedEnd
                ? [{ start: excludedStart, end: excludedEnd }]
                : [],
          },
          appInstanceId
        );
      }

      setToast?.(
        isEdit
          ? t("consulting.offeringUpdated") || "Consultation updated"
          : t("consulting.offeringCreated") || "Consultation published"
      );
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      setToast?.(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!spaceId || !productId) return;
    setSaving(true);
    try {
      const res = await deleteAppPricing(spaceId, productId);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      setToast?.(t("consulting.offeringDeleted") || "Consultation removed");
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      setToast?.(err.message || "Delete failed");
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <div
        className="absolute inset-0 z-30 flex flex-col backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consulting-offering-form-title"
      >
        <button
          type="button"
          className="absolute inset-0 bg-black/50"
          aria-label={t("common.close") || "Close"}
          onClick={onClose}
        />
        <div className="relative z-10 flex min-h-0 flex-1 items-end justify-center p-3 sm:items-center sm:p-6">
          <form
            onSubmit={handleSave}
            className={`flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-radius-md border shadow-2xl ${panel}`}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div
              className={`flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4 ${
                "border-separator-default"
              }`}
            >
              <div>
                <h2 id="consulting-offering-form-title" className={`text-title-3 ${baseText}`}>
                  {isEdit
                    ? t("consulting.editOffering") || "Edit consultation"
                    : t("consulting.newOffering") || "New consultation"}
                </h2>
                <p className={`mt-0.5 text-footnote ${muted}`}>
                  {t("consulting.offeringFormHintCalendar") ||
                    "Name, price, your available hours, session length — clients pick a slot then pay."}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`rounded-radius-xs p-2 hover:bg-fill-quaternary`}
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 campaign-window-scrollbar">
              <label className={`block text-footnote-semibold ${baseText}`}>
                {t("consulting.offeringName") || "Name"} *
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("consulting.offeringNamePh") || "e.g. 1:1 Strategy Call (45 min)"}
                className={`mt-1 w-full rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none ${input}`}
              />

              <label className={`mt-4 block text-footnote-semibold ${baseText}`}>
                {t("consulting.offeringHeadline") || "Short subtitle"}
              </label>
              <input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder={t("consulting.offeringHeadlinePh") || "What they'll get from the session"}
                className={`mt-1 w-full rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none ${input}`}
              />

              <div className="mt-4">
                <p className={`text-footnote-semibold ${baseText}`}>{t("mintSpace.price") || "Price"}</p>
                <div className="mt-2 flex gap-2">
                  {[
                    { id: "paid", label: t("mintSpace.paid") || "Paid" },
                    { id: "free", label: t("mintSpace.free") || "Free" },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setAccessType(id)}
                      className={`flex-1 rounded-radius-sm border py-2 text-footnote-semibold transition ${
                        accessType === id
                          ? "border-app-consulting-base bg-app-consulting-deep text-app-consulting-soft"
                          : "border-separator-default text-label-secondary"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {accessType === "paid" && (
                  <div className="relative mt-2">
                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-subheadline-semibold ${muted}`}>₾</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className={`w-full rounded-radius-sm border py-2.5 pl-8 pr-3 text-subheadline outline-none ${input}`}
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-3 rounded-radius-sm border border-app-consulting-base bg-app-consulting-deep p-3">
                <p className={`text-footnote-semibold ${baseText}`}>
                  {t("consulting.availabilityTitle") || "Availability"}
                </p>
                <label className={`flex items-center gap-2 text-footnote ${muted}`}>
                  <input
                    type="checkbox"
                    checked={includeWeekends}
                    onChange={(e) => setIncludeWeekends(e.target.checked)}
                  />
                  {t("consulting.includeWeekends") || "Include weekends (Sat & Sun)"}
                </label>
                <div className="space-y-2">
                  {weeklyUi
                    .filter((r) => r.day >= 1 && r.day <= 5)
                    .map((row) => (
                      <div key={row.day} className="flex flex-wrap items-center gap-2 text-footnote">
                        <label className="flex w-16 items-center gap-1">
                          <input
                            type="checkbox"
                            checked={row.on}
                            onChange={(e) =>
                              setWeeklyUi((prev) =>
                                prev.map((r) =>
                                  r.day === row.day ? { ...r, on: e.target.checked } : r
                                )
                              )
                            }
                          />
                          {WEEK_DAYS.find((d) => d.id === row.day)?.label}
                        </label>
                        <input
                          type="time"
                          value={row.start}
                          disabled={!row.on}
                          onChange={(e) =>
                            setWeeklyUi((prev) =>
                              prev.map((r) =>
                                r.day === row.day ? { ...r, start: e.target.value } : r
                              )
                            )
                          }
                          className={`rounded border px-1 py-0.5 ${input}`}
                        />
                        <span className={muted}>–</span>
                        <input
                          type="time"
                          value={row.end}
                          disabled={!row.on}
                          onChange={(e) =>
                            setWeeklyUi((prev) =>
                              prev.map((r) =>
                                r.day === row.day ? { ...r, end: e.target.value } : r
                              )
                            )
                          }
                          className={`rounded border px-1 py-0.5 ${input}`}
                        />
                      </div>
                    ))}
                </div>
                <label className={`flex items-center gap-2 text-footnote ${muted}`}>
                  <input
                    type="checkbox"
                    checked={useExcludedBlock}
                    onChange={(e) => setUseExcludedBlock(e.target.checked)}
                  />
                  {t("consulting.blockHours") || "Block hours inside the day (e.g. 17:00–18:00)"}
                </label>
                {useExcludedBlock && (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={excludedStart}
                      onChange={(e) => setExcludedStart(e.target.value)}
                      className={`rounded border px-2 py-1 text-footnote ${input}`}
                    />
                    <span className={muted}>–</span>
                    <input
                      type="time"
                      value={excludedEnd}
                      onChange={(e) => setExcludedEnd(e.target.value)}
                      className={`rounded border px-2 py-1 text-footnote ${input}`}
                    />
                  </div>
                )}
                <div>
                  <label className={`text-caption-1 ${muted}`}>
                    {t("consulting.sessionDuration") || "One consultation lasts (minutes)"}
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="180"
                    step="15"
                    value={slotMinutes}
                    onChange={(e) => setSlotMinutes(e.target.value)}
                    className={`mt-1 w-28 rounded-radius-xs border px-2 py-1.5 text-subheadline ${input}`}
                  />
                </div>
                <div>
                  <label className={`text-caption-1 ${muted}`}>{t("consulting.timezone") || "Timezone"}</label>
                  <PopUpSelect
                    label={t("consulting.timezone")}
                    value={timezone}
                    onChange={setTimezone}
                    className="mt-1 w-full"
                  >
                    {CONSULTING_TIMEZONES.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.label}
                      </option>
                    ))}
                  </PopUpSelect>
                </div>
              </div>

              <label className={`mt-4 block text-footnote-semibold ${baseText}`}>
                {t("consulting.offeringDescription") || "Description (optional)"}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
                rows={3}
                className={`mt-1 w-full resize-none rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${input}`}
              />

              <div className="mt-4">
                <p className={`text-footnote-semibold ${baseText}`}>
                  {t("consulting.coverImage") || "Cover image"}
                </p>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className={`mt-2 flex w-full items-center justify-center gap-2 rounded-radius-sm border border-dashed py-8 transition ${
                    isDark ? "border-white/15 hover:border-app-consulting-base" : "border-slate-300 hover:border-app-consulting-base"
                  }`}
                >
                  {uploading ? (
                    <FiLoader className="h-5 w-5 animate-spin" />
                  ) : listingPreview ? (
                    <img src={listingPreview} alt="" className="max-h-32 rounded-radius-xs object-cover" />
                  ) : (
                    <>
                      <FiImage className="h-5 w-5 opacity-50" />
                      <span className={`text-footnote ${muted}`}>{t("consulting.uploadCover") || "Upload cover"}</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div
              className={`flex shrink-0 flex-wrap items-center gap-2 border-t px-5 py-4 ${
                "border-separator-default"
              }`}
            >
              {isEdit && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1 rounded-radius-sm border border-status-danger px-3 py-2 text-footnote-semibold text-status-danger"
                >
                  <FiTrash2 className="h-3.5 w-3.5" />
                  {t("common.delete") || "Delete"}
                </button>
              )}
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-radius-sm border px-4 py-2 text-footnote-semibold ${
                    "border-separator-default"
                  }`}
                >
                  {t("common.cancel") || "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-radius-sm bg-app-consulting-base px-5 py-2 text-footnote-semibold text-app-consulting-on disabled:opacity-60"
                >
                  {saving && <FiLoader className="h-4 w-4 animate-spin" />}
                  {isEdit ? t("common.save") || "Save" : t("consulting.publishOffering") || "Publish"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("consulting.deleteOfferingTitle") || "Remove consultation?"}
        message={
          t("consulting.deleteOfferingMsg") ||
          "Buyers who already purchased keep their access. This removes the offering from your catalog."
        }
        confirmLabel={t("common.delete") || "Delete"}
        onConfirm={handleDelete}
        isDark={isDark}
        variant="danger"
        loading={saving}
      />
    </>
  );
}
